import type { FC, ReactNode } from 'react'
import type { ScenarioKey } from '@/constants/dashboard'

import { useMemo, useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { formatCompactCurrency } from '@/lib/format'
import { AreaChart } from '@/components/charts/area-chart'
import { DeltaBadge } from '@/components/charts/delta-badge'
import { AnimatedNumber } from '@/components/generals/animated-number'
import { FloatingCard, PreviewFrame, Segmented } from '@/components/features/preview-frame'

import {
    CASH_BALANCE,
    CHART_COLORS,
    FORECAST_LABELS,
    FORECAST_SCENARIOS,
    buildForecast
} from '@/constants/dashboard'

const OPTIONS: Array<{ value: ScenarioKey, label: string }> = [
    { value: 'conservative', label: 'Conservative' },
    { value: 'base', label: 'Base' },
    { value: 'growth', label: 'Growth' }
]

const SCENARIO_COLORS: Record<ScenarioKey, string> = {
    conservative: CHART_COLORS.terracotta,
    base: CHART_COLORS.burgundy,
    growth: CHART_COLORS.sage
}

export const ForecastPreview: FC = (): ReactNode => {
    const [scenario, setScenario] = useState<ScenarioKey>('base')
    const forecast = useMemo(() => buildForecast(scenario), [scenario])
    const projected = forecast.values[forecast.values.length - 1]
    const projection = forecast.values.slice(forecast.splitIndex + 1)
    const growth = Math.round(((projected - CASH_BALANCE) / CASH_BALANCE) * 1000) / 10
    const lowest = Math.min(...projection)
    const lowestLabel = FORECAST_LABELS[forecast.splitIndex + 1 + projection.indexOf(lowest)]

    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div>
                        <p className='text-sm font-medium'>Cash flow forecast</p>
                        <p className='text-[11px] text-stone'>Six months of actuals, six months ahead</p>
                    </div>

                    <Segmented label='Forecast scenario' value={scenario} options={OPTIONS} onChange={setScenario} />
                </div>

                <div className='mt-5 flex flex-wrap items-end gap-x-8 gap-y-3'>
                    <div>
                        <p className='text-[11px] text-stone-dark'>Projected cash, March 2027</p>

                        <p className='mt-1 font-heading text-[1.9rem] font-medium leading-none tracking-[-0.03em]'>
                            <AnimatedNumber value={projected} format='compact' />
                        </p>
                    </div>

                    <div className='pb-1'>
                        <DeltaBadge value={growth} suffix='vs today' />
                    </div>
                </div>

                <p aria-live='polite' className='mt-2 text-[11px] text-stone'>{FORECAST_SCENARIOS[scenario].note}</p>

                <AreaChart
                    title={`Cash balance forecast, ${FORECAST_SCENARIOS[scenario].label}`}
                    labels={FORECAST_LABELS}
                    series={[{ name: 'Cash balance', values: forecast.values, color: SCENARIO_COLORS[scenario], fill: true, fadeFrom: forecast.splitIndex }]}
                    band={{ low: forecast.low, high: forecast.high, color: CHART_COLORS.gold, from: forecast.splitIndex }}
                    domain={[1600000, 4000000]}
                    marker={{ index: forecast.splitIndex, label: 'Today' }}
                    height={170}
                    formatValue={formatCompactCurrency}
                    formatAxis={formatCompactCurrency}
                    className='mt-6'
                />
            </PreviewFrame>

            <FloatingCard className='-right-4 -top-7 w-56 lg:-right-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <TrendingUp aria-hidden='true' className='size-4 text-sage-dark' />
                    Lowest projected balance
                </p>

                <p className='mt-1 text-[11px] text-stone'>
                    {formatCompactCurrency(lowest)} in {lowestLabel}, well above your $1.5M floor
                </p>
            </FloatingCard>
        </div>
    )
}
